/**
 * Thin wrappers around browser platform APIs used by the app state machine:
 * WebXR support, geolocation permission, fullscreen exit, wake lock, rear camera.
 * All functions degrade gracefully (never throw synchronously).
 */

export type GeoPermission = PermissionState | 'unknown';

/** navigator.xr.isSessionSupported('immersive-ar'), false on any error. */
export async function isArSupported(): Promise<boolean> {
  try {
    return (await navigator.xr?.isSessionSupported('immersive-ar')) === true;
  } catch {
    return false;
  }
}

/**
 * True on iPhone/iPad(/iPadOS-as-Mac) Safari-family browsers: no WebXR there, so the app
 * falls back to camera mode (getUserMedia + DeviceOrientationEvent) instead of showing the
 * "kein AR" error. Primary signal: the iOS 13+ `DeviceOrientationEvent.requestPermission`
 * gate (present only on iOS/iPadOS Safari and any browser shell built on it, since they all
 * share WebKit). Fallback: a UA sniff for iPhone/iPad/iPod, or "Macintosh" with multi-touch
 * (iPadOS 13+ reports as a Mac UA but exposes touch points a real Mac never does).
 */
export function isIosLike(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  const DOE = (globalThis as { DeviceOrientationEvent?: { requestPermission?: unknown } }).DeviceOrientationEvent;
  if (DOE && typeof DOE.requestPermission === 'function') return true;
  const ua = nav.userAgent ?? '';
  if (/iP(hone|ad|od)/.test(ua)) return true;
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return true;
  return false;
}

/**
 * Current geolocation permission state; `onChange` is called on later changes.
 * Returns 'unknown' where the Permissions API is missing.
 */
export async function queryGeoPermission(onChange?: (s: GeoPermission) => void): Promise<GeoPermission> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' });
    if (!status) return 'unknown';
    if (onChange) status.addEventListener('change', () => onChange(status.state));
    return status.state;
  } catch {
    return 'unknown';
  }
}

export function exitFullscreen(): void {
  if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
    document.exitFullscreen().catch(() => undefined);
  }
}

/** Screen wake lock that is re-acquired when the page becomes visible again. */
export class WakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private wanted = false;
  private readonly onVisible = () => {
    if (this.wanted && document.visibilityState === 'visible' && !this.sentinel) void this.acquire();
  };

  request(): void {
    if (this.wanted) return;
    this.wanted = true;
    document.addEventListener('visibilitychange', this.onVisible);
    void this.acquire();
  }

  release(): void {
    this.wanted = false;
    document.removeEventListener('visibilitychange', this.onVisible);
    const s = this.sentinel;
    this.sentinel = null;
    s?.release().catch(() => undefined);
  }

  private async acquire(): Promise<void> {
    if (!('wakeLock' in navigator)) return;
    try {
      const s = await navigator.wakeLock.request('screen');
      if (!this.wanted) {
        void s.release();
        return;
      }
      this.sentinel = s;
      s.addEventListener('release', () => {
        if (this.sentinel === s) this.sentinel = null;
      });
    } catch {
      /* not allowed (e.g. page hidden, battery saver): ignore */
    }
  }
}

/**
 * Rear camera stream in a fullscreen <video> (object-fit: cover). MUST be called
 * synchronously (no leading await) from the triggering click on iOS. The autoplay-related
 * attributes must already be set on `video` before this assigns srcObject.
 */
export function openRearCamera(video: HTMLVideoElement): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(new Error('Kamera nicht verfügbar'));
  return navigator.mediaDevices
    .getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    })
    .then((stream) => {
      video.srcObject = stream;
      const play = () => video.play().catch(() => undefined);
      play();
      return stream;
    });
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}
